import React, { lazy } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ContactUs } from '../components/ContactUs';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, MessageSquare } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
export function ContactPage() {
  useScreenInit();
  const contactInfo = [{
    icon: <Phone className="text-[#54c595]" size={24} data-id="element-572" />,
    title: 'Phone',
    details: '(301) 555-0123',
    subDetails: 'Mon-Fri 9am-6pm',
    href: 'tel:3015550123'
  }, {
    icon: <Mail className="text-[#54c595]" size={24} data-id="element-573" />,
    title: 'Email',
    details: 'hello@colesvilledental.com',
    subDetails: 'Online support 24/7',
    href: 'mailto:hello@colesvilledental.com'
  }, {
    icon: <MapPin className="text-[#54c595]" size={24} data-id="element-574" />,
    title: 'Location',
    details: '8737 Colesville Road #301',
    subDetails: 'Silver Spring, MD 20910',
    href: 'https://maps.google.com'
  }];
  return <div className="min-h-screen bg-white" data-id="element-575">
      <Header data-id="element-576" />

      <main className="pt-32" data-id="element-577">
        {/* Hero Section */}
        <section className="bg-slate-50 py-20 px-4" data-id="element-578">
          <div className="max-w-7xl mx-auto text-center" data-id="element-579">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 bg-[#cef4d2]/50 px-4 py-2 rounded-full mb-6" data-id="element-580">
              <MessageSquare size={16} className="text-[#54c595]" data-id="element-581" />
              <span className="text-[#225274] font-bold text-sm tracking-widest uppercase" data-id="element-582">
                Get in Touch
              </span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-6xl font-bold text-[#225274] mb-6" data-id="element-583">
              Contact Our Friendly Team
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed" data-id="element-584">
              We're here to help with any questions about your dental care,
              insurance, or scheduling. Reach out to us today!
            </motion.p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-16 px-4 -mt-12 relative z-10" data-id="element-585">
          <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8" data-id="element-586">
            {contactInfo.map((info, idx) => <motion.a key={idx} href={info.href} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3 + idx * 0.1
          }} className="bg-white p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col items-center text-center hover:scale-105 transition-transform duration-300" data-id="element-587">
                <div className="w-16 h-16 bg-[#cef4d2]/30 rounded-2xl flex items-center justify-center mb-6" data-id="element-588">
                  {info.icon}
                </div>
                <h3 className="text-xl font-bold text-[#225274] mb-2" data-id="element-589">
                  {info.title}
                </h3>
                <p className="text-lg font-semibold text-[#225274] mb-1" data-id="element-590">
                  {info.details}
                </p>
                <p className="text-slate-500 text-sm" data-id="element-591">{info.subDetails}</p>
              </motion.a>)}
          </div>
        </section>

        {/* Main Contact Form Section */}
        <ContactUs data-id="element-592" />

        {/* Map / Hours Section */}
        <section className="py-24 px-4 bg-slate-50" data-id="element-593">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center" data-id="element-594">
            <div className="space-y-8" data-id="element-595">
              <div data-id="element-596">
                <h2 className="text-3xl font-bold text-[#225274] mb-6" data-id="element-597">
                  Office Hours
                </h2>
                <div className="space-y-4" data-id="element-598">
                  {[{
                  day: 'Monday',
                  hours: '9:00 AM - 5:00 PM'
                }, {
                  day: 'Tuesday',
                  hours: '2:00 PM - 7:00 PM'
                }, {
                  day: 'Wednesday',
                  hours: '9:00 AM - 2:00 PM'
                }, {
                  day: 'Thursday',
                  hours: '2:00 PM - 7:00 PM'
                }, {
                  day: 'Friday',
                  hours: '2:00 PM - 7:00 PM'
                }, {
                  day: 'Saturday',
                  hours: 'By Appointment'
                }, {
                  day: 'Sunday',
                  hours: 'Closed'
                }].map((item, i) => <div key={i} className="flex justify-between items-center border-b border-slate-200 pb-2" data-id="element-599">
                      <span className="font-bold text-[#225274]" data-id="element-600">
                        {item.day}
                      </span>
                      <span className="text-slate-600" data-id="element-601">{item.hours}</span>
                    </div>)}
                </div>
              </div>
              <div className="bg-[#225274] p-8 rounded-3xl text-white" data-id="element-602">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-603">
                  <Clock size={20} className="text-[#54c595]" data-id="element-604" />
                  Emergency Care
                </h3>
                <p className="text-white/80 leading-relaxed" data-id="element-605">
                  If you are experiencing a dental emergency after hours, please
                  call our main line and follow the prompts for urgent care
                  assistance.
                </p>
              </div>
            </div>
            <div className="h-[500px] bg-slate-200 rounded-[40px] overflow-hidden shadow-2xl relative" data-id="element-606">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3101.0234567890123!2d-77.03!3d39.01!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDAwJzM2LjAiTiA3N8KwMDEnNDguMCJX!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus&q=8737+Colesville+Road+%23301,+Silver+Spring,+MD+20910" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Colesville Dental Center Location" className="w-full h-full" data-id="element-607" />
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-608" />
    </div>;
}